import { Button } from '@/components/atoms/Button';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { getCachedSession } from '@/features/auth/utils/sessionCache';
import { useAllFilterStore } from '@/features/product/stores/AllFilterStore';
import { SearchBox } from '@/features/search/components/organisms/SearchBox';
import { trackEvent } from '@/shared/utils/mixpanel';
import { cn } from '@/styles/utils';
import { Session } from 'next-auth';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { NavDropdownButton } from '../molecules/NavDropdownButton';

export function Navigation({ className }: { className?: string }) {
  const [session, setSession] = useState<Session | null>(null);
  const { setShowAllFilters } = useAllFilterStore();
  const router = useRouter();

  useEffect(() => {
    const fetchSession = async () => {
      const currentSession = await getCachedSession();
      setSession(currentSession);
    };
    fetchSession();
  }, []);

  const handleHomeClick = () => {
    // 사용자가 했던 액션이 보전되지 않도록 수정
    setShowAllFilters(false);

    // onboarding 진입을 위한 쿠키 삭제
    document.cookie = 'hasVisited=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    // 홈 화면으로 이동
    window.location.href = '/';
  };

  const handleLogout = () => {
    trackEvent('Button Click', { 'Page Name': '홈 화면', 'Button Name': '로그아웃' });
    useAuthStore.getState().signOut();
  };

  const handleSignIn = () => {
    trackEvent('Button Click', { 'Page Name': '홈 화면', 'Button Name': '로그인' });
    router.push('/signin');
  };

  const handleSignUp = () => {
    trackEvent('Button Click', { 'Page Name': '홈 화면', 'Button Name': '회원가입' });
    router.push('/signup');
  };

  const handleAccountClick = () => {
    trackEvent('Button Click', { 'Page Name': '홈 화면', 'Button Name': '마이페이지' });
    router.push('/account');
  };

  // 축산 정보 드롭다운 항목
  const livestockInfoItems = [
    { label: '검역량', href: '/livestock_import', gtagEvent: 'livestockimport', requiresAuth: true },
    { label: '오퍼가 계산기', href: '/offer-calculator', gtagEvent: 'offercalculator', requiresAuth: true },
    { label: '통관환율', href: '/currency', gtagEvent: 'currency', requiresAuth: true },
    { label: 'AI 축산 신문', href: '/ai-news', gtagEvent: 'ainews', requiresAuth: true },
    { label: '창고 지도', href: '/warehouses', gtagEvent: 'warehouse', requiresAuth: true },
    { label: '채권추심', href: '/bond-banner', target: '_blank', gtagEvent: 'bondbanner', requiresAuth: false },
    { label: 'EZ PAY', href: '/ez-pay', gtagEvent: 'ezpay', requiresAuth: false },
    { label: '수입대행', href: 'https://docs.google.com/forms/d/e/1FAIpQLSekJqYF91-WWnLpEzoWoTKld4a81z3_AuXcX9JUpHx2VK9Nag/viewform?usp=header', target: '_blank', gtagEvent: 'importagent', requiresAuth: false },
    { label: '축산전문배차', href: 'https://docs.google.com/forms/d/e/1FAIpQLSfRdcqiE5y4yumTnoObcTyQs-RvCYQZBlMKDmLWfAg-y5cVlQ/viewform?usp=header', target: '_blank', gtagEvent: 'livestockdispatch', requiresAuth: false },
    { label: '수정/문의사항', href: 'https://forms.gle/Y8mxtwoKkSQYza2G6', target: '_blank', gtagEvent: 'contactus', requiresAuth: true },
  ];

  // 축산 정보 드롭다운 버튼 컴포넌트
  const LivestockInfoDropdown = () => (
    <NavDropdownButton
      label="축산 정보"
      items={livestockInfoItems}
    />
  );

  return (
    <nav
      className={cn(
        'flex items-center justify-between h-[88px] px-4',
        className,
      )}
    >
      {/* 로고 (왼쪽 정렬) */}
      <Image
        src="/images/logo.svg"
        alt="easymeat"
        width={144.29}
        height={24.58}
        className="cursor-pointer"
        onClick={handleHomeClick}
      />

      {/* SearchBox (가운데 정렬) */}
      <div className="flex justify-center">
        <Suspense fallback={
          <div className="w-full max-w-xl mx-auto">
            <div className="w-full h-12 bg-gray-100 rounded-lg animate-pulse" />
          </div>
        }>
          <SearchBox />
        </Suspense>
      </div>

      {/* 버튼들 (오른쪽 정렬) */}
      <div className="flex items-center gap-5">
        {session?.user ? (
          <>
            <span className="text-[#222222] title-20sb font-bold! text-lg!">
              {session.user.name}님 환영합니다.
            </span>
            <Button variant="ghost" size="lg" onClick={handleLogout} className="text-[#1D232B] font-bold! text-xl!">
              로그아웃
            </Button>
            <LivestockInfoDropdown />
            <Button variant="ghost" size="lg" onClick={handleAccountClick} className="text-[#1D232B] font-bold! text-xl!">
              마이페이지
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={handleSignIn} className="text-[#1D232B] font-bold! text-xl!">
              로그인
            </Button>
            <LivestockInfoDropdown />
            <Button variant="ghost" size="lg" onClick={handleSignUp} className="text-[#1D232B] font-bold! text-xl!">
              회원가입
            </Button>
          </>
        )}
      </div>
    </nav>
  );
}
